import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaHome, FaFolder, FaFileAlt, FaLink, FaTicketAlt, FaHeadset, FaCog, FaSignOutAlt, FaTimes, FaCommentDots, FaAd, FaGlobeAmericas
} from 'react-icons/fa';

const DashboardSidebar = ({ 
  sidebarOpen, 
  setSidebarOpen, 
  onShowLogoutDialog,
  onShowFeedbackModal,
  supportTickets = []
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Calculate open tickets count
  const openTicketsCount = supportTickets ? supportTickets.filter(ticket => ticket.status !== 'closed' && ticket.status !== 'resolved').length : 0;
  const hasOpenTickets = openTicketsCount > 0;

  // Get current menu item from URL
  const getCurrentMenuItem = () => {
    const currentPath = location.pathname;
    if (currentPath === '/dashboard') return 'dashboard';
    if (currentPath.startsWith('/dashboard/projects')) return 'projects';
    if (currentPath.startsWith('/dashboard/documents')) return 'documents';
    if (currentPath.startsWith('/dashboard/shortlinks')) return 'shortlinks';
    if (currentPath.startsWith('/dashboard/landing-pages')) return 'landing-pages';
    if (currentPath.startsWith('/dashboard/internal-ads')) return 'internal-ads';
    if (currentPath === '/dashboard/support') return 'support';
    if (currentPath.startsWith('/dashboard/support/tickets')) return 'tickets';
    if (currentPath.startsWith('/dashboard/settings')) return 'settings';
    return 'dashboard';
  };

  return (
    <>
      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-black transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:sticky lg:top-0 flex flex-col h-screen`}>
        {/* Header - Fixed */}
        <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center">
            <img src="/icon.svg" alt="AppActs" className="h-8 w-8" />
            <span className="ml-3 text-white font-semibold">Dashboard</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-400 hover:text-white"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>
        
        {/* Navigation - Scrollable */}
        <nav className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-3 py-6">
            <div className="space-y-1">
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'dashboard'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaHome className="mr-3 h-5 w-5" />
                Dashboard
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/projects');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'projects'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaFolder className="mr-3 h-5 w-5" />
                Projects
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/documents');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'documents'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaFileAlt className="mr-3 h-5 w-5" />
                Documents
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/shortlinks');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'shortlinks'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaLink className="mr-3 h-5 w-5" />
                Short Links
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/internal-ads');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'internal-ads'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaAd className="mr-3 h-5 w-5" />
                Internal Ads
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/support/tickets');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'tickets'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaTicketAlt className={`mr-3 h-5 w-5 ${hasOpenTickets ? 'text-red-400 animate-pulse' : ''}`} />
                Support Tickets
                {hasOpenTickets && (
                  <span className="ml-2 px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded-full animate-pulse">
                    {openTicketsCount}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  navigate('/dashboard/landing-pages');
                }}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  getCurrentMenuItem() === 'landing-pages'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <FaGlobeAmericas className="mr-3 h-5 w-5" />
                <span className="flex-1 text-left">Landing Pages</span>
              </button>
            </div>
          </div>
        </nav>
        
        {/* Bottom menu items - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-800 px-3 py-3">
          <div className="space-y-1">
            <button
              onClick={() => {
                setSidebarOpen(false);
                if (onShowFeedbackModal) {
                  onShowFeedbackModal(true);
                }
              }}
              className="w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              <FaCommentDots className="mr-3 h-5 w-5" />
              FeedBack&Support
            </button>
            
            <button
              onClick={() => {
                setSidebarOpen(false);
                navigate('/dashboard/support');
              }}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                getCurrentMenuItem() === 'support'
                    ? 'bg-gray-700 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <FaHeadset className="mr-3 h-5 w-5" />
              Support Modules
            </button>
            
            <button
              onClick={() => {
                setSidebarOpen(false);
                navigate('/dashboard/settings');
              }}
              className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                getCurrentMenuItem() === 'settings'
                    ? 'bg-gray-700 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <FaCog className="mr-3 h-5 w-5" />
              Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardSidebar;
